import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TopNav } from "./TopNav.js";
import {
  createWaitingListLead,
  dismissWaitingListSignup,
  fetchWaitingList,
  type WaitingListSignup,
  type WaitingListStatus,
} from "../api.js";
import { setView } from "../store/view.js";

// ===========================================================================
// Waiting List — staff view of raw Discount Saver signups (B_TFLEXIBLE_SAVER).
//
// The hourly WF-WAITING-LIST notifier only WhatsApps a signup when a
// *discounted* slot appears near their postcode, which is rare — so most
// signups sit silent and were previously invisible to staff. This view shows
// them, with a live-availability probe per row so you can see why someone
// hasn't auto-notified (e.g. techs available but none discount-anchored), and
// lets you push a signup into the pipeline or dismiss it.
// ===========================================================================

type Props = { userName: string };

const STATUS_META: Record<
  WaitingListStatus,
  { label: string; dot: string; text: string }
> = {
  waiting: { label: "Waiting", dot: "#ECB22E", text: "text-slack-yellow" },
  notified: { label: "Notified", dot: "#36C5F0", text: "text-slack-cyan" },
  in_pipeline: { label: "In pipeline", dot: "#2EB67D", text: "text-slack-green" },
};

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86_400_000;
  const days = Math.floor(diff / day);
  if (days <= 0) {
    const hrs = Math.floor(diff / 3_600_000);
    return hrs <= 0 ? "just now" : `${hrs}h ago`;
  }
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1mo ago" : `${months}mo ago`;
}

function LiveCell({ live }: { live: WaitingListSignup["live"] }): JSX.Element {
  if (!live) return <span className="text-ink-400">—</span>;
  if (live.discounted > 0) {
    return (
      <span className="text-slack-green">
        {live.slots} slots · {live.discounted} discounted
      </span>
    );
  }
  if (live.slots > 0) {
    return (
      <span className="text-ink-100">
        {live.slots} slots · <span className="text-ink-300">0 discounted</span>
      </span>
    );
  }
  return <span className="text-ink-400">no availability</span>;
}

function CountPill({
  label,
  value,
  dot,
}: {
  label: string;
  value: number;
  dot: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-700/60 px-3 py-2">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: dot }}
      />
      <span className="text-lg font-semibold text-ink-50">{value}</span>
      <span className="text-[12px] uppercase tracking-[0.12em] text-ink-200">
        {label}
      </span>
    </div>
  );
}

export function WaitingList({ userName }: Props): JSX.Element {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  const q = useQuery({
    queryKey: ["waiting-list"],
    queryFn: fetchWaitingList,
    refetchOnWindowFocus: false,
  });

  const createLead = useMutation({
    mutationFn: (id: number) => createWaitingListLead(id),
    onSettled: () => {
      setBusyId(null);
      void qc.invalidateQueries({ queryKey: ["waiting-list"] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: number) => dismissWaitingListSignup(id),
    onSettled: () => {
      setBusyId(null);
      void qc.invalidateQueries({ queryKey: ["waiting-list"] });
    },
  });

  const signups = q.data?.signups ?? [];
  const counts = q.data?.counts;

  function copyNumber(s: WaitingListSignup): void {
    void navigator.clipboard?.writeText(s.whatsapp).then(() => {
      setCopied(s.id);
      window.setTimeout(() => setCopied((c) => (c === s.id ? null : c)), 1500);
    });
  }

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Waiting List
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                Discount saver signups
              </h1>
              <p className="mt-1 text-[13px] text-ink-200">
                Customers who asked to be notified when a discounted slot opens
                near them. They&apos;re only auto-messaged when a discount-anchored
                slot appears in their area, so most sit here waiting.
              </p>
            </div>
            <button
              onClick={() => void q.refetch()}
              disabled={q.isFetching}
              className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400 hover:bg-ink-700 disabled:opacity-60"
            >
              {q.isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {counts && (
            <div className="flex flex-wrap gap-3">
              <CountPill label="Waiting" value={counts.waiting} dot="#ECB22E" />
              <CountPill label="Notified" value={counts.notified} dot="#36C5F0" />
              <CountPill label="In pipeline" value={counts.inPipeline} dot="#2EB67D" />
            </div>
          )}

          <section className="overflow-hidden rounded-xl border border-ink-700 bg-ink-700/40 shadow-card">
            {q.isLoading ? (
              <div className="p-6 text-[13px] text-ink-200">loading…</div>
            ) : q.isError ? (
              <div className="p-6 text-[13px] text-slack-pink">
                Couldn&apos;t load the waiting list. Try Refresh.
              </div>
            ) : signups.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-ink-300">
                No signups yet. They&apos;ll appear here the moment someone joins the
                waiting list from the /quote page.
              </div>
            ) : (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-ink-700 text-left text-[11px] uppercase tracking-[0.12em] text-ink-300">
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Postcode</th>
                    <th className="px-4 py-3 font-semibold">Signed up</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Live availability</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s) => {
                    const meta = STATUS_META[s.status];
                    const rowBusy = busyId === s.id;
                    return (
                      <tr
                        key={s.id}
                        className="border-b border-ink-700/60 last:border-0 hover:bg-ink-700/30"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-ink-50">{s.firstName}</div>
                          <button
                            onClick={() => copyNumber(s)}
                            title="Copy number"
                            className="mt-0.5 text-[12px] text-ink-300 hover:text-ink-100"
                          >
                            {s.whatsapp}{" "}
                            <span className="text-ink-400">
                              {copied === s.id ? "✓ copied" : "⧉"}
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-ink-100">{s.postcode}</td>
                        <td className="px-4 py-3 text-ink-200" title={s.signedUpAt ?? ""}>
                          {relTime(s.signedUpAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-2 ${meta.text}`}>
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: meta.dot }}
                            />
                            {meta.label}
                          </span>
                          {s.status === "notified" && s.notifiedSlot && (
                            <div className="mt-0.5 text-[11px] text-ink-400">
                              offered {s.notifiedSlot}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <LiveCell live={s.live} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {s.status === "in_pipeline" ? (
                              <button
                                onClick={() => setView("pipeline")}
                                className="rounded-md border border-slack-green/50 px-2.5 py-1 text-[12px] text-slack-green hover:bg-slack-green/10"
                              >
                                Open in pipeline →
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setBusyId(s.id);
                                  createLead.mutate(s.id);
                                }}
                                disabled={rowBusy}
                                className="rounded-md bg-slack-aubergine px-2.5 py-1 text-[12px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-60"
                              >
                                {rowBusy && createLead.isPending ? "Creating…" : "Create lead"}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setBusyId(s.id);
                                dismiss.mutate(s.id);
                              }}
                              disabled={rowBusy}
                              className="rounded-md border border-ink-500 px-2.5 py-1 text-[12px] text-ink-200 hover:border-ink-400 hover:bg-ink-700 disabled:opacity-60"
                            >
                              {rowBusy && dismiss.isPending ? "…" : "Dismiss"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <p className="text-[12px] text-ink-400">
            &ldquo;Live availability&rdquo; probes the scheduling service for the next 60
            days. A signup is only auto-notified when at least one slot is
            <span className="text-ink-300"> discount-flagged</span> — techs being
            merely available isn&apos;t enough, so a row can show availability and
            still correctly sit in Waiting.
          </p>
        </div>
      </main>
    </div>
  );
}
